<template>
  <div class="text-center">

    <error-dialog :status="loadStatus" />
    <error-dialog :status="placeOrderStatus" />
    <error-dialog :status="onUpgradeSubscriptionPriceErrorStatus" />

    <sign-in-dialog v-model="showSignIn" />

    <org-sign-up-dialog
      v-if="resolveOrgSignup"
      hide-email hide-subtitle
      :value="true"
      @cancel="resolveOrgSignup(false)"
      @complete="resolveOrgSignup(true)"
    />

    <!-- Note that we deliberately don't gate this with a v-if to preseve the contents of its org sign-up form -->
    <payment-dialog
      v-if="showUpgradeSubscription"
      title="Upgrade subscription"
      details="You will be billed automatically on an annual basis. You can manage your subscription preferences and cancel at any time from your account."
      :subtitle="`<div class='mt-6 overline short'>BUSINESS | $${selectedBusinessPlan.price} per year</div><ul><li class='ml-n2'>${(selectedBusinessPlan.max == 3) ? '1-3' : ('up to ' + selectedBusinessPlan.max)} shared users</li><li class='ml-n2'>${selectedBusinessPlan.sends} sends per year (includes group-signers and recipients)</li></ul>`"
      :metadata="{
        target: 'SUBSCRIPTION-UPGRADE',
        code: 'BUSINESS',
        max: selectedBusinessPlan.max,
        cadence: 'ANNUAL'
      }"
      v-model="showUpgradeSubscription"
      @complete="
        (uPaymentId, uMetadata, uItemized) => {
          showUpgradeSubscription = false;
          onComplete(uPaymentId, uMetadata, uItemized);
        }
      "
      @price-error="onUpgradeSubscriptionPriceError"
    />

    <template v-if="!isSignedIn">
      <div v-if="!showSignIn">
        <v-btn text color="primary" @click="showSignIn = true">
          <v-icon class="mr-1" v-html="mdiAccount" />
          Sign in / create account…
        </v-btn>
        <div class="mt-3 text--label-active">Sign in / create account to complete checkout</div>
      </div>
    </template>

    <template v-else>

      <div class="mx-auto px-4" style="width: 432px; max-width: 100%;">
        <v-btn
          v-if="isSignedIn && !hideSignOut"
          class="justify-start overflow-hidden"
          style="letter-spacing: normal; text-transform: none; max-width: 100%;"
          text color="primary"
          :disabled="disableSignOut"
          @click="showSignIn = true"
        >
          <v-icon class="mr-1" v-html="mdiAccountCheck" />
          {{global.profile.email}}
          <span class="btn-tooltip bottom">Sign out / switch accounts</span>
        </v-btn>

        <v-btn
          class="d-block my-6 pt-6 pb-3 fill-width rounded-xl"
          style="height: unset;"
          text tile
          :loading="loadStatus.pending || onCompleteStatus.pending"
          :disabled="!itemizedResolved"
          :aria-expanded="String(showItemized)"
          @click="showItemized = !showItemized"
        >
          <div class="fill-width">
            <div
              class="pb-3 d-flex justify-center align-center primary--text"
              v-bind:style="{ opacity: itemizedResolved ? 1 : 0 }"
            >
              <price class="text-h2 mr-3" :value="itemizedResolved?.price" />
              <v-icon class="menu-down" large v-html="mdiMenuDown" />
            </div>
            <v-expand-transition>
              <div v-if="itemizedResolved && showItemized">
                <itemized-price
                  class="mt-6"
                  style="text-transform: none !important; text-align: left;"
                  :itemized="itemizedResolved"
                  :total="false"
                />
              </div>
            </v-expand-transition>
          </div>
          <span class="btn-tooltip top">{{showItemized ? 'Hide' : 'Show'}} breakdown</span>
        </v-btn>
      </div>

      <template v-if="!loadExistingSubscriptionStatus.pending && !didStartCompleting">

        <div ref="height" class="mx-auto relative" style="max-width: 400px; transition: height .3s .1s;">

          <div v-if="itemizedResolved" v-el-resize="({ height }) => $refs.height.style.height = height + 'px'" class="absolute fill-width px-3">
            <!-- The overall price is $0.00, this order can be placed directly -->
            <v-btn
              v-if="!priceToCents(itemizedResolved.price)"
              color="primary"
              :loading="placeOrderStatus.pending"
              @click="placeOrder(metadataResolved, itemizedResolved)"
            >
              Place order
            </v-btn>

            <div
              v-else-if="nonGiftcardPriceCents && (priceToCents(itemizedResolved.price) < priceToCents(minimum))"
              class="text-body-2 px-4"
            >
              <div class="mx-auto grey--text" style="max-width: 400px;">
                <template v-if="minimum == '1.00'">Ellacard does not allow charges below $1.00. </template>
                <template v-else>You don't have enough credit in your Ellacard account to complete this transaction. </template>

                <template v-if="isEligibleForSubscription">
                  <template v-if="existingSubscription">
                    <a href="javascript:void(0)" @click="show = [ 'Upgrade your subscription' ]">Upgrade your subscription</a> or <a href="javascript:void(0)" @click="show = [ 'Or pre-pay for discounted Ellacard credit' ]">add credit</a> to continue.
                  </template>
                  <template v-else>
                    <a href="javascript:void(0)" @click="show = [ 'Pre-pay for discounted Ellacard credit' ]">Add credit</a> or <a href="javascript:void(0)" @click="show = [ 'Or become a subscriber' ]">become a subscriber</a> to continue.
                  </template>
                </template>
                <template v-else>
                  Add credit to continue.
                </template>
              </div>
            </div>

            <payment-btns
              v-else
              class="my-0"
              :metadata="metadataResolved"
              :itemized="itemizedResolved"
              @metadata-error="e => $emit('metadata-error', e)"
              @price-update="onPriceUpdate"
              @complete="x => onComplete(x, metadataResolved, itemizedResolved)"
            />
          </div>
        </div>



        <title-toggle
          v-if="isEligibleForSubscription && existingSubscription"
          class="mt-12"
          title="Upgrade your subscription"
          :offset="false"
          v-model="show"
        >
          <div class="px-1 px-sm-2 mx-auto d-flex justify-center" style="max-width: 1200px;">
            <v-card
              class="mx-1 mx-sm-2 d-flex flex-column text-center"
              style="width: 50%; max-width: 300px;"
              hover disabled color="grey lighten-4"
            >
              <div class="text--label-active pa-2 grey white--text">Your current plan</div>
              <v-divider />
              <v-card-title
                class="justify-center"
                style="text-transform: capitalize;"
                v-text="existingSubscription.metadata.code.toLowerCase()"
              />
              <div>
                <price price-class="text-h4" :value="existingSubscription.next_price" per="year" />
              </div>
              <div
                v-if="[ 'INDIVIDUAL', 'BUSINESS' ].includes(existingSubscription.metadata.code)"
                class="mt-4 text-body-2 primary--text"
              >
                {{nExistingSends.toLocaleString('en-US')}} sends<span class="hidden-xs-only">&nbsp;per year</span>
              </div>
              <div
                v-if="existingSubscription.metadata.code == 'BUSINESS' || true"
                class="grey--text text--label-active"
                style="margin-top: 2px;"
              >
                <template v-if="existingSubscription.metadata.max == 3">1-3</template>
                <template v-else>up to {{existingSubscription.metadata.max}}</template>
                <span class="hidden-xs-only">&nbsp;shared</span> users
              </div>
            </v-card>

            <v-card
              class="mx-1 mx-sm-2 relative text-center pb-6" style="width: 50%; max-width: 300px;"
              hover
              @click="showUpgradeSubscription = true"
            >
              <v-card-title class="justify-center" style="margin-top: 32px;">Business</v-card-title>
              <div class="px-3">
                <price
                  price-class="text-h4"
                  prefix="+ "
                  :value="selectedSubscrptionBusinessPlanPrice - existingSubscription.next_price"
                  per="year"
                />
              </div>
              <div class="pb-12 px-3">
                <v-menu bottom offset-y absolute>
                  <template v-slot:activator="{ on, attrs }">
                    <v-card
                      v-bind="attrs" v-on="on"
                      class="mt-2 d-inline-block py-1"
                      style="border-color: #1976d2;"
                      outlined
                    >
                      <div class="text-body-2 px-3 primary--text text-center" style="white-space: nowrap;">
                        +{{(selectedBusinessPlan.sends - nExistingSends).toLocaleString('en-US')}} sends<span class="hidden-xs-only">&nbsp;per year</span> <v-icon class="menu-down ml-n1 my-n1" color="primary" v-html="mdiMenuDown" />
                      </div>
                      <div class="grey--text text--label-active" style="margin-top: 2px;">
                        <template v-if="selectedBusinessPlan.max == 3">1-3</template>
                        <template v-else>up to {{selectedBusinessPlan.max}}</template>
                        <span class="hidden-xs-only">&nbsp;shared</span> users
                      </div>
                    </v-card>
                  </template>
                  <v-list dense>
                    <v-list-item
                      v-for="item in eligibleUpgradePlans"
                      :key="item.price"
                      class="text-body-2 py-2"
                      @click="selectedSubscrptionBusinessPlanPrice = item.price"
                    >
                      <div class="flex-grow-1">
                        <div>+{{(item.sends - nExistingSends).toLocaleString('en-US')}} sends per year</div>
                        <div class="grey--text text--label-active">
                          up to {{item.max}} shared users
                        </div>
                      </div>
                      <div class="ml-6 primary--text" style="white-space: nowrap;">
                        + <price :value="item.price - existingSubscription.next_price" />
                      </div>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <v-btn color="primary">Upgrade</v-btn>
            </v-card>
          </div>
          <div class="mt-6 grey--text text-body-2">
            See our <router-link target="_blank" :to="{ name: 'pricing', hash: '#subscriptions' }">pricing page</router-link> for more details
          </div>
        </title-toggle>



        <title-toggle
          v-if="nonGiftcardPriceCents && (nonGiftcardPriceCents < priceToCents(minimum))"
          ref="CREDIT-PACK"
          class="mt-12"
          :title="((isEligibleForSubscription && existingSubscription) ? 'Or p' : 'P') + 're-pay for discounted Ellacard credit'"
          :offset="false"
          v-model="show"
        >
          <div class="px-4 mx-auto credit-pack-grid">
            <v-card
              v-for="pack in creditPacks" :key="pack.amt"
              v-if="(pack.amt * 100) > nonGiftcardPriceCents"
              class="relative text-center pb-sm-3 d-flex align-center d-sm-block"
              hover
              :dark="isSelected({ amt: pack.amt + '.00' })"
              :color="isSelected({ amt: pack.amt + '.00' }) ? 'primary' : null"
              @click="setMetadata({ target: 'CREDIT-PACK', amt: pack.amt + '.00' })"
            >
              <v-btn
                v-if="isSelected({ amt: pack.amt + '.00' })"
                style="right: -12px; top: -12px;"
                fab absolute small light
                @click.stop="metadataResolved = metadata"
              >
                <v-icon v-html="mdiClose" />
              </v-btn>
              <v-card-title class="text-body-1 text-sm-h6 justify-center">${{pack.amt}} of credit</v-card-title>
              <v-spacer />
              <price
                class="text-body-1 text-sm-h4"
                :color="isSelected({ amt: pack.amt + '.00' }) ? 'white' : null"
                :value="pack.price"
              />
              <div class="ma-3">
                <v-chip
                  class="justify-center"
                  style="width: 72px;"
                  label outlined small
                  :color="pack.discount ? (isSelected({ amt: pack.amt + '.00' }) ? 'white' : 'green') : 'transparent'"
                  v-text="pack.discount + '% off'"
                />
              </div>
            </v-card>
          </div>
          <div class="mt-6 grey--text text-body-2">
            <template v-if="global.profile?.org_id">Credit will be shared across your entire organization. </template>Pre-paid credit cannot be applied to gift cards.
          </div>
        </title-toggle>



        <title-toggle
          v-if="isEligibleForSubscription && !existingSubscription"
          ref="SUBSCRIPTION"
          class="mt-12"
          :title="(nonGiftcardPriceCents && (nonGiftcardPriceCents < priceToCents(minimum))) ? 'Or become a subscriber' : 'Do you want to add a subscription?'"
          :offset="false"
          v-model="show"
        >
          <div class="px-1 px-sm-2 mx-auto d-flex justify-center" style="max-width: 1200px;">
            <v-card
              class="mx-1 mx-sm-2 relative text-center"
              style="width: 50%; max-width: 300px;"
              hover
              :dark="isSelected({ code: 'INDIVIDUAL' })"
              :color="isSelected({ code: 'INDIVIDUAL' }) ? 'primary' : null"
              @click="setMetadata({ target: 'SUBSCRIPTION', code: 'INDIVIDUAL', cadence: 'ANNUAL' })"
            >
              <v-btn
                v-if="isSelected({ code: 'INDIVIDUAL' })"
                style="right: -12px; top: -12px;"
                fab absolute small light
                @click.stop="metadataResolved = metadata"
              >
                <v-icon v-html="mdiClose" />
              </v-btn>
              <v-card-title class="justify-center">Individual</v-card-title>
              <price
                class="text-h4"
                value="40" per="year"
                :color="isSelected({ code: 'INDIVIDUAL' }) ? 'white' : null"
              />
              <div
                class="mt-3 text-body-2 px-3 text-center"
                :class="(isSelected({ code: 'INDIVIDUAL' }) ? 'white' : 'primary') + '--text'"
                style="border: 1px solid transparent;"
              >
                500 sends<span class="hidden-sm-and-down">&nbsp;per year</span>
              </div>
              <v-card-subtitle class="relative" style="margin-top: 32px;">
                <div class="absolute" style="width: calc(100% - 32px);">
                  For those looking to create and send multiple greeting cards / invitations for personal use
                </div>
                <div class="transparent--text user-select-none" v-text="LONGEST_DESCRIPTION" />
              </v-card-subtitle>
              <v-divider />
              <v-card-text class="text-left">
                <ul class="ml-n2">
                  <li>Create unlimited cards</li>
                  <li>500 sends per year (includes group-signers and recipients)</li>
                  <li>Unlimited shares via link / social media</li>
                </ul>
              </v-card-text>
            </v-card>

            <v-card
              class="mx-1 mx-sm-2 relative text-center hidden-xs-only"
              style="width: 50%; max-width: 300px;"
              hover
              :dark="isBusinessBasicSelected"
              :color="isBusinessBasicSelected ? 'primary' : null"
              @click="setMetadata({ target: 'SUBSCRIPTION', code: 'BUSINESS', cadence: 'ANNUAL', max: 3 })"
            >
              <v-btn
                v-if="isBusinessBasicSelected"
                style="right: -12px; top: -12px;"
                fab absolute small light
                @click.stop="metadataResolved = metadata"
              >
                <v-icon v-html="mdiClose" />
              </v-btn>
              <v-card-title class="justify-center" style="white-space: nowrap;">Business basic</v-card-title>
              <price
                class="text-h4"
                value="60" per="year"
                :color="isBusinessBasicSelected ? 'white' : null"
              />
              <div class="mt-2 py-1" style="border: 1px solid transparent;">
                <div
                  class="text-body-2 px-3 text-center"
                  :class="(isBusinessBasicSelected ? 'white' : 'primary') + '--text'"
                >
                  750 sends<span class="hidden-sm-and-down">&nbsp;per year</span>
                </div>
                <div
                  class="text--label-active"
                  :class="(isBusinessBasicSelected ? 'white' : 'grey') + '--text'"
                  style="margin-top: 2px;"
                >
                  1-3<span class="hidden-sm-and-down">&nbsp;shared</span> users
                </div>
              </div>
              <v-card-subtitle class="mt-3 relative">
                <div class="absolute" style="width: calc(100% - 32px);">
                  For 1 – 3 administrators to create and send greeting cards / invitations on behalf of their organization
                </div>
                <div class="transparent--text user-select-none" v-text="LONGEST_DESCRIPTION" />
              </v-card-subtitle>
              <v-divider />
              <v-card-text class="text-left">
                <ul class="ml-n2">
                  <li>Create unlimited cards</li>
                  <li>750 sends per year (includes group-signers and recipients)</li>
                  <li>Unlimited shares via link / social media</li>
                  <li>Access to business tools</li>
                </ul>
              </v-card-text>
            </v-card>

            <v-card
              class="mx-1 mx-sm-2 relative text-center"
              style="width: 50%; max-width: 300px;"
              hover
              :dark="isBusinessPlusSelected"
              :color="isBusinessPlusSelected ? 'primary' : null"
              @click="setMetadata({
                target: 'SUBSCRIPTION',
                code: 'BUSINESS',
                cadence: 'ANNUAL',
                max: selectedBusinessPlan.max
              })"
            >
              <v-btn
                v-if="isBusinessPlusSelected"
                style="right: -12px; top: -12px;"
                fab absolute small light
                @click.stop="metadataResolved = metadata"
              >
                <v-icon v-html="mdiClose" />
              </v-btn>
              <v-card-title class="justify-center">
                Business<span class="hidden-sm-and-down">&nbsp;plus</span>
              </v-card-title>
              <price
                class="text-h4"
                :color="isBusinessPlusSelected ? 'white' : null"
                :value="selectedSubscrptionBusinessPlanPrice"
                per="year"
              />
              <div class="px-3">
                <v-menu bottom offset-y absolute>
                  <template v-slot:activator="{ on, attrs }">
                    <v-card
                      v-bind="attrs" v-on="on"
                      class="mt-2 d-inline-block py-1"
                      v-bind:style="{ 'border-color': isBusinessPlusSelected ? '#FFF !important' : '#1976d2' }"
                      outlined
                      :dark="isBusinessPlusSelected"
                      :color="isBusinessPlusSelected ? 'primary' : null"
                    >
                      <div
                        class="text-body-2 px-3 text-center"
                        :class="(isBusinessPlusSelected ? 'white' : 'primary') + '--text'"
                        style="white-space: nowrap;"
                      >
                        {{selectedBusinessPlan.sends.toLocaleString('en-US')}} sends<span class="hidden-sm-and-down"> per year</span> <v-icon class="menu-down ml-n1 my-n1" :color="isBusinessPlusSelected ? 'white' : 'primary'" v-html="mdiMenuDown" />
                      </div>
                      <div
                        class="text--label-active"
                        :class="(isBusinessPlusSelected ? 'white' : 'grey') + '--text'"
                        style="margin-top: 2px;"
                      >
                        <template v-if="selectedBusinessPlan.max == 3">1-3</template>
                        <template v-else>up to {{selectedBusinessPlan.max}}</template>
                        <span class="hidden-sm-and-down"> shared</span> users
                      </div>
                    </v-card>
                  </template>
                  <v-list dense>
                    <v-list-item
                      v-for="item in ($vuetify.breakpoint.xsOnly ? BUSINESS_PLANS : BUSINESS_PLANS.slice(1))"
                      :key="item.price"
                      class="text-body-2 py-2"
                      @click="selectedSubscrptionBusinessPlanPrice = item.price"
                    >
                      <div class="flex-grow-1">
                        <div>{{item.sends.toLocaleString('en-US')}} sends<span class="hidden-xs-only">&nbsp;per year</span></div>
                        <div class="grey--text text--label-active">
                          up to {{item.max}}<span class="hidden-xs-only">&nbsp;shared</span> users
                        </div>
                      </div>
                      <div class="ml-6 primary--text" style="white-space: nowrap;">
                        <price :value="item.price" />
                      </div>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <v-card-subtitle class="mt-3 relative">
                <div class="absolute" style="width: calc(100% - 32px);">
                  <template v-if="selectedBusinessPlan.max == 3">
                    For a few administrators to create and send greeting cards / invitations on behalf of their organization
                  </template>
                  <template v-else>{{LONGEST_DESCRIPTION}}</template>
                </div>
                <div class="transparent--text" v-text="LONGEST_DESCRIPTION" />
              </v-card-subtitle>
              <v-divider />
              <v-card-text class="text-left">
                <ul class="ml-n2">
                  <li>Create unlimited cards</li>
                  <li>{{selectedBusinessPlan.sends.toLocaleString('en-US')}} sends per year (includes group-signers and recipients)</li>
                  <li>Unlimited shares via link / social media</li>
                  <li>Access to business tools</li>
                  <li v-if="selectedSubscrptionBusinessPlanPrice > 60">Access to API</li>
                </ul>
              </v-card-text>
            </v-card>

          </div>
        </title-toggle>

      </template>

      <v-progress-circular v-else class="mx-auto" indeterminate />
    </template>

  </div>
</template>


<style scoped>
.credit-pack-grid {
  display: grid;
  grid-gap: 16px;
  max-width: 1200px;
}
@media (min-width: 600px) {
  .credit-pack-grid {
    grid-auto-flow: column;
  }
}
</style>


<script>
import ItemizedPrice from '@/components/ItemizedPrice.vue';
import OrgSignUpDialog from '@/components/OrgSignUpDialog.vue';
import OrgSignUpForm from '@/components/OrgSignUpForm.vue';
import PaymentBtns from '@/components/PaymentBtns.vue';
import PaymentDialog from '@/components/PaymentDialog.vue';
import Price from '@/components/Price.vue';
import SignInDialog from '@/components/SignInDialog.vue';
import TitleToggle from '@/components/TitleToggle.vue';
import { LOCALE } from '@/utils/intl.js';
import { OPError } from '@/utils/op-dev-fetch';
import { mdiAccount, mdiAccountCheck, mdiClose, mdiCash100, mdiDomain, mdiMenuDown, mdiMenuUp, mdiSwapHorizontalVariant, mdiAlert } from '@mdi/js';


const GIFTCARD_KEYS = [ 'GIFTCARD', 'GIFTCARD_FEE', 'CREDIT_GIFTCARD' ];

function priceToCents(price) {
  return parseInt(price.replace('.', ''));
}

function centsToPrice(cents) {
  const asStr = String(cents).padStart(3, '0');
  return asStr.slice(0, -2) + '.' + asStr.slice(-2);
}


const BUSINESS_PLANS = [
  { max: 3, price: 60, sends: 750 },
  { max: 25, price: 150, sends: 2000 },
  { max: 50, price: 300, sends: 3000 },
  { max: 75, price: 450, sends: 4000 },
  { max: 100, price: 600, sends: 5000 },
  { max: 125, price: 750, sends: 6000 },
  { max: 150, price: 900, sends: 7000 },
  { max: 175, price: 1050, sends: 8000 },
  { max: 200, price: 1200, sends: 9000 },
  { max: 225, price: 1350, sends: 10000 },
  { max: 250, price: 1500, sends: 11000 }
];


const LONGEST_DESCRIPTION = 'For businesses that want to share credit among employees so they can all create and send greeting cards / invitations';


export default {
  name: 'payment',

  components: {
    ItemizedPrice,
    OrgSignUpDialog,
    OrgSignUpForm,
    PaymentBtns,
    PaymentDialog,
    Price,
    SignInDialog,
    TitleToggle
  },

  props: {
    metadata: Object,
    minimum: { type: String, default: '5.00' },
    disableSignOut: Boolean,
    hideSignOut: Boolean,
    passiveSignIn: Boolean
  },

  data() {
    return {
      metadataResolved: this.metadata,
      existingSubscription: null,
      selectedSubscrptionBusinessPlanPrice: 60,
      show: [
        'Upgrade your subscription',
        'Pre-pay for discounted Ellacard credit',
        'Do you want to add a subscription?'
      ],
      showSignIn: false,
      showItemized: false,
      showPayNow: false,
      showAddSubscription: false,
      showPaymentBtns: false,
      showUpgradeSubscription: false,
      resolveOrgSignup: null,
      itemized: null,
      itemizedResolved: null,
      didStartCompleting: false,
      loadStatus: this.$track('load'),
      loadExistingSubscriptionStatus: this.$track('loadExistingSubscription'),
      placeOrderStatus: this.$track('placeOrder'),
      onCompleteStatus: this.$track('onComplete'),
      onUpgradeSubscriptionPriceErrorStatus: this.$track('onUpgradeSubscriptionPriceError')
    };
  },

  computed: {
    creditPacks() {
      const smallest = (this.minimum == '1.00') ? 1 : 10;
      return [
        { amt: smallest, price: smallest, discount: 0 },
        { amt: 20, price: 18, discount: 10 },
        { amt: 50, price: 40, discount: 20 },
        { amt: 100, price: 70, discount: 30 }
      ];
    },

    isBusinessBasicSelected() {
      return this.isSelected({ code: 'BUSINESS', max: 3 });
    },

    isBusinessPlusSelected() {
      return this.isSelected({ code: 'BUSINESS' }) && (this.$vuetify.breakpoint.xsOnly || (this.metadataResolved.max > 3));
    },

    loadCondition() {
      return [ this.metadataResolved, this.globalUserId ];
    },

    loadExistingSubscriptionCondition() {
      return [ this.globalUserId, this.isEligibleForSubscription ];
    },

    nonGiftcardPriceCents() {
      return (this.itemized?.items || []).filter(
        x => !GIFTCARD_KEYS.includes(x.key)).reduce((acc, x) => acc + priceToCents(x.unit) * x.qty, 0
      );
    },

    isEligibleForSubscription() {
      if (!this.metadata || !this.itemized)
        return false;
      if (!this.global.profile || (this.global.profile.org_id && !this.global.profile.org_admin))
        return false;
      if (![ 'CARD', 'CARD-BATCH', 'ADDTL-SENDS' ].includes(this.metadata.target) || !this.nonGiftcardPriceCents)
        return false;
      return true;
    },

    isEligibleForSubsciptionUpgrade() {
      return this.isEligibleForSubscription && this.existingSubscription;
    },

    nExistingSends() {
      if (!this.existingSubscription)
        return 0;
      return (this.existingSubscription.metadata.code == 'INDIVIDUAL') ? 500 : BUSINESS_PLANS.find(x => x.max == this.existingSubscription.metadata.max)?.sends;
    },

    nAddtlSendsRequired() {
      return this.itemized?.items.reduce((a, x) => a + ([ 'CONTRIBUTOR', 'RECIPIENT' ].includes(x.key) ? x.qty : 0), 0) || 0;
    },

    eligibleUpgradePlans() {
      return BUSINESS_PLANS.filter(x => (x.sends - this.nExistingSends) >= this.nAddtlSendsRequired);
    },

    selectedBusinessPlan() {
      return BUSINESS_PLANS.find(x => x.price == this.selectedSubscrptionBusinessPlanPrice);
    }
  },

  created() {
    Object.assign(this, { BUSINESS_PLANS, LONGEST_DESCRIPTION, priceToCents, centsToPrice, mdiAccount, mdiAccountCheck, mdiClose, mdiCash100, mdiDomain, mdiMenuDown, mdiMenuUp, mdiAlert });
  },

  methods: {
    isSelected(partial) {
      const resolved = this.metadataResolved;
      if (Object.keys(partial).every(x => partial[x] == resolved[x]))
        return true;
      if (resolved.credit && Object.keys(partial).every(x => partial[x] == resolved.credit[x]))
        return true;
      return false;
    },

    async onUpgradeSubscriptionPriceError(e) {
      this.payment = null;
      if (e.type == 'ClientException')
        throw new VisibleError(e.message);
      throw e;
    },

    async getItemized(metadata) {
      try {
        const itemized = await this.$call('get-itemized-price', { metadata, locale: LOCALE });
        $event.log('price-loaded', { metadata, ...itemized });
        return itemized;
      } catch (e) {
        if ((e instanceof OPError) && (e.type == 'InvalidMetadataException'))
          this.$emit('metadata-error', e);
        else
          throw e;
      }
    },

    async load() {
      this.showPayNow = false;
      this.itemizedResolved = null;
      if (!this.isSignedIn || !this.metadata) {
        this.itemized = null;
        return;
      }

      if (!this.itemized)
        this.itemized = await this.getItemized(this.metadata);

      if (this.metadataResolved === this.metadata)
        this.itemizedResolved = this.itemized;
      else
        this.itemizedResolved = await this.getItemized(this.metadataResolved);
    },

    async setMetadata(metadata) {
      // For subscriptions / upgrades, use this method instead of setting 'metadataResolved' directly in order to
      // handle org status automatically. Note that there are a couple of cases that we don't handle that should never
      // be presented in the UI: non-org admins shouldn't see any subscription-related options at all.

      if ((metadata.code == 'BUSINESS') && !this.global.profile.org_id) {
        const r = await new Promise(r => this.resolveOrgSignup = r);
        this.resolveOrgSignup = null;
        if (!r)
          // The user failed to create an org
          return;
      }

      else if ((metadata.code == 'INDIVIDUAL') && this.global.profile.org_admin) {
        const r = await this.$confirm({
          title: 'Delete organization?',
          msg: `This plan is for individuals, but you administer an Ellacard organization. If you haven't used any business features yet and don't need access to them, you can delete the organization and continue. Do you want to delete your organization?`,
          buttons: [ 'Cancel', 'Delete' ],
          primary: 'Delete'
        });
        if (r == 'Cancel')
          return;
        const { success, msg } = await this.$call('delete-empty-org');
        if (!success && (msg == 'COMPLETED_ORDER'))
          throw new VisibleError('Unable to delete organization – orders have already been completed');
        if (!success && (msg == 'ORG_INVITE'))
          throw new VisibleError('Unable to delete organization – please remove all invited users first');

        await this.$refreshJWT();
      }

      if ([ 'CARD', 'CARD-BATCH' ].includes(this.metadata.target)) {
        // These cases can be resolved as a single payment by adding the subscription as "credit"
        this.metadataResolved = { ...this.metadata, credit: metadata };
      } else {
        this.metadataResolved = metadata;
      }
    },

    onPriceUpdate() {
      this.$confirm({ msg: 'Price has been updated, please try again', buttons: [ 'Ok' ], primary: 'Ok' });
      this.itemized = null;
      this.load();
    },

    async placeOrder(metadata, itemized) {
      try {
        const params = { metadata, locale: LOCALE, items: itemized.items };
        const response = await this.$call('fulfill-no-payment', params);
        if (response.price_update)
          this.onPriceUpdate();
        else
          this.onComplete(response, metadata, itemized);
      } catch (e) {
        if ((e instanceof OPError) && (e.type == 'InvalidMetadataException'))
          this.$emit('metadata-error', e);
        else
          throw e;
      }
    },

    async onComplete(payment_id, metadata, itemized) {
      this.didStartCompleting = true;

      // Note that 'payment_id' may be undefined if we failed the idempotency check
      if (metadata.target == 'CREDIT-PACK')
        this.$notify('Credit added');
      else if (metadata.target == 'SUSBCRIPTION')
        this.$notify('Subscription added');
      else if (metadata.target == 'SUSBCRIPTION-UPGRADE')
        this.$notify('Subscription upgraded' + (this.existingSubscription.cancelled_at ? '<br><br>Your subscription has been automatically resumed – cancel any time from your account' : ''));

      $event.log('checkout', { metadata, ...itemized, payment_id });

      try {
        if (metadata.target != this.metadata.target) {
          // We just upsold something else and still need to complete the original payment
          this.itemized = await this.getItemized(this.metadata);
          if (priceToCents(this.itemized.price))
            this.didStartCompleting = false;
          else
            await this.placeOrder(this.metadata, this.itemized);
        } else {
          this.$emit('complete');
        }
      } catch (e) {
        this.didStartCompleting = false;
      }
    },

    async loadExistingSubscription() {
      this.existingSubscription = await this.$call('get-subscription');
    }
  },

  watch: {
    loadExistingSubscriptionCondition: {
      immediate: true,
      handler(v) {
        this.existingSubscription = null;
        if (v.every(x => x))
          this.loadExistingSubscription();
      }
    },

    isEligibleForSubsciptionUpgrade: {
      immediate: true,
      handler(v) {
        this.$emit('update:isEligibleForSubsciptionUpgrade', !!v);
      }
    },

    eligibleUpgradePlans(v) {
      if (this.isEligibleForSubscription && this.existingSubscription)
        this.selectedSubscrptionBusinessPlanPrice = v[0].price;
    },

    isSignedIn: {
      immediate: true,
      handler(v) {
        if (!v)
          this.showSignIn = !this.passiveSignIn;
      }
    },

    '$vuetify.breakpoint.xsOnly': {
      immediate: true,
      handler(v) {
        if (this.isEligibleForSubscription && this.existingSubscription)
          return;
        if (v) {
          if (this.selectedCard == 1) {
            this.selectedCard = 2;
            this.selectedSubscrptionBusinessPlanPrice = 60;
          } else if (this.selectedCard == -1) {
            this.selectedSubscrptionBusinessPlanPrice = 60;
          }
        } else {
          if ((this.selectedCard == 2) && (this.selectedSubscrptionBusinessPlanPrice == 60))
            this.selectedCard = 1;
          if (this.selectedSubscrptionBusinessPlanPrice == 60)
            this.selectedSubscrptionBusinessPlanPrice = 150;
        }
      }
    },

    show(v) {
      const resolved = this.metadataResolved;
      const targets = [ resolved.target, resolved.credit?.target ];
      if ([ 'CREDIT-PACK', 'SUBSCRIPTION' ].find(x => !v.includes(this.$refs[x]?.$options.propsData.title) && targets.includes(x)))
        this.metadataResolved = this.metadata;
    },

    metadata(cur, prev) {
      this.showPayNow = false;
      this.itemized = null;
      this.itemizedResolved = null;
      this.metadataResolved = cur;
    },

    loadCondition: {
      immediate: true,
      handler() {
        this.load();
      }
    },

    didStartCompleting(v) {
      this.$emit('did-start-completing', v);
    }
  }
}
</script>
